var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { marked } from 'marked';
// import confetti from 'canvas-confetti';
import axiosInstance from '@/config/axiosInstance';
import { v4 as uuidv4 } from 'uuid';
// /**
//  * Triggers a confetti effect.
//  */
// const triggerConfetti = () => {
//    confetti({
//       particleCount: 100,
//       spread: 70,
//       origin: { y: 0.6 }
//    });
// };
export const calculateTokenCount = (content) => {
    // Simple token count based on word count
    return content.split(/\s+/).length;
};
/**
 * Creates markup from a string using marked.
 * @param {string} content - The content to convert to HTML.
 * @returns {object} The HTML markup.
 */
export const createMarkup = (content) => {
    const htmlContent = marked(content);
    return { __html: htmlContent };
};
/**
 * Scrolls to the bottom of the messages container smoothly.
 * @param {RefObject<HTMLDivElement | null>} ref - The messages container reference.
 */
export const scrollToBottom = (ref) => {
    if (ref.current) {
        ref.current.scrollTop = ref.current.scrollHeight; // Scroll to the bottom
        // Force reflow
        void ref.current.offsetHeight; // Use void to avoid ESLint error
    }
};
/**
 * Handles toggling sources visibility.
 * @param {string} messageId - The message ID.
 * @param {Dispatch<SetStateAction<{ [key: string]: boolean }>>} setShowSources - Function to set show sources state.
 */
export const handleToggleSources = (messageId, setShowSources) => {
    setShowSources(prevShowSources => (Object.assign(Object.assign({}, prevShowSources), { [messageId]: !prevShowSources[messageId] })));
};
/**
 * Handles closing sources.
 * @param {string} messageId - The message ID.
 * @param {Dispatch<SetStateAction<{ [key: string]: boolean }>>} setShowSources - Function to set show sources state.
 */
export const handleCloseSources = (messageId, setShowSources) => {
    setShowSources(prevShowSources => (Object.assign(Object.assign({}, prevShowSources), { [messageId]: false })));
};
/**
 * Clears all old conversations and their corresponding titles.
 * @param {Dispatch<SetStateAction<Conversation[]>>} setConversations - Function to set conversations.
 */
export const clearOldConversations = (setConversations) => {
    const conversations = JSON.parse(localStorage.getItem('conversations') || '[]');
    const conversationIDs = conversations.map(conversation => conversation.conversationID);
    // Clear conversations from local storage
    localStorage.removeItem('conversations');
    // Remove corresponding chat titles from local storage
    conversationIDs.forEach(id => {
        localStorage.removeItem(id);
    });
    setConversations([]); // Clear the conversations state
};
/**
 * Deletes an individual conversation and its corresponding title.
 * @param {string} conversationID - The ID of the conversation to delete.
 * @param {Dispatch<SetStateAction<Conversation[]>>} setConversations - Function to set conversations.
 */
export const deleteConversation = (conversationID, setConversations) => {
    setConversations(prevConversations => {
        const updatedConversations = prevConversations.filter(conversation => conversation.conversationID !== conversationID);
        // Update local storage with the new conversations array
        localStorage.setItem('conversations', JSON.stringify(updatedConversations));
        // Remove the chat title from local storage
        localStorage.removeItem(conversationID);
        return updatedConversations;
    });
};
/**
 * Constructs a SharePoint URL from a source.
 * @param {Source} source - The source object.
 * @returns {string} The constructed SharePoint URL.
 */
export const constructSharePointUrl = (source) => {
    var _a;
    const documentId = source.DocumentID;
    const cleanedDocumentId = documentId.replace(/-en-US/gi, '');
    const fileExtension = ((_a = cleanedDocumentId.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || '';
    let prefix;
    switch (fileExtension) {
        case 'ppt':
        case 'pptx':
            prefix = ':p:';
            break;
        case 'pdf':
            prefix = ':b:';
            break;
        case 'mp4':
            prefix = ':v:';
            break;
        default:
            prefix = ':u:';
    }
    const encodedDocumentPath = encodeURIComponent(cleanedDocumentId.replace(/\\/g, '/')).replace(/[!'()*]/g, escape);
    const sharePointBaseUrl = `https://hennesandmauritz.sharepoint.com/${prefix}/r/sites/SupportLearning/Shared%20Documents/Learning%20Experience/Digital%20Learning%20Tools/AI%20ChatGPT_Shared/`;
    return `${sharePointBaseUrl}${encodedDocumentPath}`;
};
export const startNewChat = (setMessages, setFeedback, onNewConversationId, setConversationID, ws, conversations, setConversations, userOID) => __awaiter(void 0, void 0, void 0, function* () {
    const newConversationID = uuidv4(); // Generate a unique conversation ID
    const initialMessage = {
        type: 'chat',
        messageID: 'welcome-msg',
        content: 'Hello!<br><br>' +
            'I am the Generative AI Product Learning Chatbot, your virtual assistant for navigating tools and processes at Assortment.<br><br>' +
            'Feel free to ask me any questions connected to learning and ways of working.<br><br>' +
            'Here are some examples of what you can ask:<br>' +
            '- Is it possible to delete a product in Centric PLM?<br>' +
            '- As a Product Developer, how do I add an article in Canvas?<br>' +
            '- As a Pattern Maker, how do I open my m-chart window in Modaris?<br><br>' +
            'If you have questions about live system performances or need support, please turn to ' +
            '<a href="mailto:AssortmentSupportandLearning@hm.com" style="color: darkblue;">AssortmentSupportandLearning@hm.com</a>, ' +
            '<a href="https://engage.cloud.microsoft/main/org/hm.com/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiI5MzgwNjAxODU2In0/new" style="color: darkblue;">Plan, Build & Buy Support</a> or ' +
            '<a href="https://engage.cloud.microsoft/main/org/hm.com/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIyMTg2NjkwMzk2MTYifQ/all" style="color: darkblue;">Product Creation Support</a>.<br><br>' +
            '<span style="font-size:10px; line-height:1.2; display:inline-block;"><i>' +
            'This generative AI tool is here to assist you with inquiries related to Assortment&#39;s tools and processes.<br>' +
            'Keep in mind that while we strive for accuracy, some answers (including automated translations) may contain errors.<br>' +
            'This chatbot may use your input for development and analysis purposes (e.g. aggregate common issues or questions raised by your team or office).<br>' +
            'Please do not input any personal, financial or sensitive data into the chatbot.<br>' +
            'Further information in relation to how your data (e.g. your role, team or office location) is handled can be found in the privacy notice provided by your employer.' +
            '</i></span>',
        msgType: 'assistant',
        timestamp: new Date().toISOString(),
        sources: [],
        feedback: {},
        conversationID: newConversationID,
        userOID
    };
    // Store the current conversation in the conversations state
    const currentConversation = {
        type: 'conversation',
        conversationID: newConversationID,
        userOID: userOID || '',
        messages: [initialMessage],
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString() // You can update this later
    };
    // Update conversations state
    setConversations(prevConversations => {
        const updatedConversations = [...prevConversations, currentConversation];
        // Save updated conversations to local storage
        localStorage.setItem('conversations', JSON.stringify(updatedConversations));
        return updatedConversations;
    });
    // Reset messages and feedback
    setMessages([initialMessage]);
    setFeedback({});
    setConversationID(newConversationID);
    // Call updateOldChats to ensure existing conversations are updated
    updateOldChats([], setConversations); // Pass an empty array or relevant messages if needed
    onNewConversationId(newConversationID);
});
/**
 * Selects an old chat.
 * @param {string} conversationID - The ID of the conversation to select.
 * @param {Conversation[]} conversations - The list of conversations.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 * @param {Dispatch<SetStateAction<boolean>>} setIsSidebarOpen - Function to set sidebar open state.
 * @param {Dispatch<SetStateAction<string>>} setConversationID - Function to set the conversation ID.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to set feedback state.
 */
export const selectOldChat = (conversationID, conversations, setMessages, setIsSidebarOpen, setConversationID, // Add setConversationID parameter
setFeedback // Add setFeedback parameter
) => {
    const selectedConversation = conversations.find(conversation => conversation.conversationID === conversationID);
    if (selectedConversation) {
        setMessages(selectedConversation.messages);
        setConversationID(conversationID); // Set the selected conversation ID
        // setIsSidebarOpen(false); // Close the sidebar after selecting a chat
        // Update the feedback state
        const feedbackState = {};
        selectedConversation.messages.forEach(message => {
            feedbackState[message.messageID] = message.feedback;
        });
        setFeedback(feedbackState);
    }
    else {
        console.error('Conversation not found:', conversationID);
    }
};
/**
 * Filters and formats the messages into the required structure for LLMs and scrubs HTML tags.
 * @param {Message[]} messages - The current messages.
 * @param {string} conversationID - The current conversation ID.
 * @returns {Object[]} - The formatted messages for LLMs.
 */
const formatMessagesForLLM = (messages, conversationID) => {
    // Filter messages by conversation ID and exclude the welcome message by messageID
    const filteredMessages = messages.filter(msg => msg.conversationID === conversationID && msg.messageID !== "welcome-msg");
    return filteredMessages.map(msg => {
        // Remove HTML tags using a regex
        const cleanContent = msg.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim(); // Also replace non-breaking spaces
        return {
            role: msg.msgType === 'user' ? 'user' : 'system',
            content: cleanContent
        };
    });
};
/**
 * Handles sending a message.
 * @param {React.FormEvent} e - The form event.
 * @param {string} inputMessage - The input message.
 * @param {WebSocket | null} ws - The WebSocket instance.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 * @param {Dispatch<SetStateAction<boolean>>} setIsBotThinking - Function to set bot typing state.
 * @param {Dispatch<SetStateAction<string>>} setInputMessage - Function to set input message.
 * @param {Function} scrollToBottom - Function to scroll to the bottom.
 * @param {RefObject<HTMLDivElement | null>} messagesContainerRef - The messages container reference.
 * @param {boolean} isBotTyping - Indicates if the bot is currently typing.
 * @param {Dispatch<SetStateAction<Conversation[]>>} setConversations - Function to set conversations.
 * @param {Message[]} messages - The current messages.
 * @param {string} conversationID - The conversation ID.
 * @param {UserMetadata} userMetadata - The user metadata.
 */
export const handleSendMessage = (e, inputMessage, ws, setMessages, setIsBotThinking, setInputMessage, scrollToBottom, messagesContainerRef, isBotTyping, setConversations, conversations, messages, // Ensure this parameter is correctly passed
conversationID, userMetadata) => {
    e.preventDefault();
    if (isBotTyping) {
        console.warn('Cannot send message while bot is typing.');
        return;
    }
    if (!ws) {
        console.error('WebSocket is not connected.');
        return;
    }
    const messageContent = inputMessage.trim();
    if (messageContent && ws.readyState === WebSocket.OPEN) {
        const tokenCount = 0; // Calculate token count
        const userMessage = {
            type: 'chat',
            messageID: Date.now().toString(),
            content: messageContent,
            msgType: 'user',
            timestamp: new Date().toISOString(),
            sources: [],
            feedback: {
                feedbackType: 0,
                showCommentBox: false,
                feedbackComment: '',
                copied: false,
                supportInvoked: false
            },
            input_tokens: tokenCount,
            output_tokens: 0,
            conversationID,
            userMetadata
        };
        // Format the entire conversation history for LLMs, filtering by conversation ID
        const formattedMessages = formatMessagesForLLM([...messages, userMessage], conversationID);
        // Send the user message and the formatted conversation history
        const messageData = {
            userMessage: userMessage,
            conversationHistory: formattedMessages
        };
        ws.send(JSON.stringify(messageData));
        setMessages(prevMessages => {
            const updatedMessages = [...prevMessages, userMessage];
            // Sort messages by timestamp to maintain order
            updatedMessages.sort((a, b) => {
                const dateA = new Date(a.timestamp);
                const dateB = new Date(b.timestamp);
                return dateA.getTime() - dateB.getTime();
            });
            // Update the old chats with the new user message
            updateOldChats(updatedMessages, setConversations);
            return updatedMessages;
        });
        setIsBotThinking(true);
        setInputMessage('');
        // Use a timeout to ensure the DOM updates before scrolling
        setTimeout(() => {
            scrollToBottom(messagesContainerRef);
        }, 0);
    }
    else {
        console.error('WebSocket is not connected or not ready.');
    }
};
// /**
//  * Formats email addresses within a text.
//  * @param {string} text - The input text to format.
//  * @returns {string} - The formatted text with email addresses.
//  */
// const formatEmails = (text: string): string => {
//    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi;
//    return text.replace(emailRegex, '<a href="mailto:$1">$1</a>');
// };
// /**
//  * Formats links within a text to open in a new tab.
//  * @param {string} text - The input text to format.
//  * @returns {string} - The formatted text with links.
//  */
// const formatLinks = (text: string): string => {
//    const linkRegex = /(https?:\/\/[^\s]+)/g; // Regex to match URLs
//    return text.replace(linkRegex, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
// };
/**
 * Decodes a document ID.
 * @param {string} encodedId - The encoded document ID.
 * @returns {string} - The decoded document ID.
 */
export const decodeDocumentId = (encodedId) => {
    let decodedId = encodedId;
    let previousId;
    // Keep decoding until the value no longer changes
    do {
        previousId = decodedId;
        decodedId = decodeURIComponent(decodedId);
    } while (decodedId !== previousId);
    return decodedId;
};
// /**
//  * Formats the message content for readability.
//  * @param {string} text - The input text to format.
//  * @returns {string} - The formatted text.
//  */
// const formatMessageContent = (text: string): string => {
//    // Format emails
//    let formattedContent = formatEmails(text);
//    // Format links and apply dark-blue-link class
//    formattedContent = formatLinks(formattedContent);
//    // Convert Markdown to HTML
//    formattedContent = marked(formattedContent, { gfm: true, breaks: true }) as string; // Type assertion
//    // Decode document IDs in the formatted content
//    formattedContent = formattedContent.replace(
//       /documentID: ([^<\s]+)/g, // Adjust regex to match your document ID format
//       (match, docId) => `documentID: ${decodeDocumentId(docId)}`
//    );
//    // Use a regular expression to find all links and wrap them with the dark-blue-link class
//    formattedContent = formattedContent.replace(
//       /<a href="(.*?)">(.*?)<\/a>/g,
//       `<a href="$1" class="dark-blue-link" target="_blank" rel="noopener noreferrer">$2</a>`
//    );
//    // Additional formatting can be done here if needed
//    return formattedContent;
// };
// Function to add target="_blank" to all links in the HTML content
const addTargetBlankToLinks = (htmlContent) => {
    return htmlContent.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" '); // Add target="_blank" and rel attributes
};
/**
 * Handles incoming WebSocket messages and updates the application state accordingly.
 *
 * This function processes messages received from a WebSocket connection, including error messages,
 * chunked responses, and final messages. It updates the state of the application to reflect the latest
 * messages, sources, and feedback. The function also manages the visibility of the bot's typing indicator
 * and scrolls the message container to the bottom when new messages are added.
 *
 * @param {MessageEvent} event - The WebSocket message event containing the data received from the server.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to update feedback state.
 * @param {Dispatch<SetStateAction<boolean>>} setIsBotThinking - Function to set the bot's thinking state.
 * @param {Dispatch<SetStateAction<{ [key: string]: Source[] }>>} setSources - Function to update sources state.
 * @param {Dispatch<SetStateAction<string | null>>} setSelectedMessageId - Function to set the currently selected message ID.
 * @param {Dispatch<SetStateAction<boolean>>} setIsTypingComplete - Function to indicate if typing is complete.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to update the messages state.
 * @param {function} scrollToBottom - Function to scroll the message container to the bottom.
 * @param {RefObject<HTMLDivElement | null>} messagesContainerRef - Reference to the messages container for scrolling.
 * @param {Dispatch<SetStateAction<Conversation[]>>} setConversations - Function to update conversations state.
 * @param {Message[]} messages - The current list of messages in the conversation.
 * @param {string} conversationID - The unique identifier for the conversation.
 * @param {UserMetadata} userMetadata - Metadata about the user interacting with the chatbot.
 * @param {Conversation[]} conversations - The current list of conversations.
 * @param {function} updateOldChats - Function to update old chats with new messages.
 * @param {function} saveConversationsToLocalStorage - Function to save conversations to local storage.
 *
 * @returns {void} This function does not return a value; it updates the application state directly.
 *
 * @throws {Error} If there is an error during message parsing or state updates.
 */
let buffer = '';
export const handleReceiveMessage = (event, setFeedback, setIsBotThinking, setSources, setSelectedMessageId, setIsTypingComplete, setMessages, scrollToBottom, messagesContainerRef, setConversations, messages, conversationID, userMetadata, conversations, updateOldChats, saveConversationsToLocalStorage) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        let receivedMessage;
        try {
            receivedMessage = JSON.parse(event.data);
        }
        catch (error) {
            console.error('Error parsing JSON:', error);
            return;
        }
        if (receivedMessage.type === 'ping') { // backend server sends ping
            return;
        }
        // Handle error messages
        if (receivedMessage.msgType === 'error') {
            const htmlContent = yield marked(receivedMessage.content.trim());
            setIsBotThinking(false);
            buffer = ''; // Clear the buffer
            setMessages(prevMessages => {
                const updatedMessages = [
                    ...prevMessages,
                    {
                        type: receivedMessage.Type,
                        messageID: receivedMessage.messageID,
                        content: htmlContent,
                        msgType: 'assistant',
                        timestamp: new Date().toISOString(),
                        sources: [],
                        used_sources: [],
                        feedback: {
                            feedbackType: 0,
                            showCommentBox: false,
                            feedbackComment: '',
                            copied: false,
                            supportInvoked: false
                        },
                        conversationID,
                        userMetadata: Object.assign(Object.assign({}, userMetadata), { userOID: receivedMessage.userOID || userMetadata.userOID }),
                        input_tokens: 0,
                        output_tokens: 0
                    }
                ];
                // Call updateOldChats with the updated messages
                updateOldChats(updatedMessages, setConversations);
                return updatedMessages;
            });
            return; // Exit the function after handling the error
        }
        const messageID = receivedMessage.messageID;
        if (!messageID && receivedMessage.type !== 'ping') {
            console.error('Received message does not contain a messageID:', receivedMessage);
            return;
        }
        // Handle chunked messages
        if (receivedMessage.type === 'chunk') {
            setIsBotThinking(false); // Set to true while receiving chunks
            buffer += receivedMessage.content; // Append chunk to buffer
            // Update the existing message in the state
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                const existingMessageIndex = updatedMessages.findIndex(msg => msg.messageID === messageID);
                if (existingMessageIndex !== -1) {
                    // Update the existing message with the new content
                    updatedMessages[existingMessageIndex] = Object.assign(Object.assign({}, updatedMessages[existingMessageIndex]), { content: buffer, msgType: 'assistant', timestamp: new Date().toISOString() // Update timestamp if necessary
                     });
                }
                else {
                    // If the message doesn't exist, create a new one (shouldn't happen in this case)
                    updatedMessages.push({
                        type: 'chunk',
                        messageID,
                        content: buffer,
                        msgType: 'assistant',
                        timestamp: new Date().toISOString(),
                        sources: [],
                        used_sources: [],
                        feedback: {
                            feedbackType: 0,
                            showCommentBox: false,
                            feedbackComment: '',
                            copied: false,
                            supportInvoked: false
                        },
                        conversationID,
                        userMetadata: Object.assign(Object.assign({}, userMetadata), { userOID: receivedMessage.userOID || userMetadata.userOID }),
                        input_tokens: 0,
                        output_tokens: 0
                    });
                }
                return updatedMessages;
            });
            return; // Exit the function, waiting for more data
        }
        // Handle completion of chunks
        if (receivedMessage.type === 'allChunksReceived') {
            // Process buffered message...
            const htmlContent = yield marked(buffer.trim());
            const formattedContent = addTargetBlankToLinks(htmlContent);
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                const existingMessageIndex = updatedMessages.findIndex(msg => msg.messageID === messageID);
                if (existingMessageIndex !== -1) {
                    // Update the existing message with the final formatted content
                    updatedMessages[existingMessageIndex] = Object.assign(Object.assign({}, updatedMessages[existingMessageIndex]), { content: formattedContent });
                }
                // Check if this is the last message in the list
                if (existingMessageIndex === -1) {
                    setIsBotThinking(false); // Set to false if this is the last message
                }
                return updatedMessages;
            });
            buffer = ''; // Clear the buffer
            return; // Exit the function after processing all chunks
        }
        // Handle final messages
        if (receivedMessage.type === 'chat' || receivedMessage.type === 'greeting') {
            const htmlContent = yield marked(receivedMessage.content); // Convert Markdown to HTML
            const formattedContent = addTargetBlankToLinks(htmlContent);
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                const existingMessageIndex = updatedMessages.findIndex(msg => msg.messageID === messageID);
                if (existingMessageIndex !== -1) {
                    // Update the existing message with new content and sources
                    updatedMessages[existingMessageIndex] = Object.assign(Object.assign({}, updatedMessages[existingMessageIndex]), { content: formattedContent, sources: receivedMessage.sources || [], used_sources: receivedMessage.used_sources || [], type: 'chat' });
                }
                else {
                    // If the message doesn't exist, add it as a new message
                    updatedMessages.push(Object.assign(Object.assign({}, receivedMessage), { content: formattedContent, sources: receivedMessage.sources || [], used_sources: receivedMessage.used_sources || [] }));
                }
                // Call updateOldChats with the updated messages
                updateOldChats(updatedMessages, setConversations);
                // Check if this is the last message in the list
                if (existingMessageIndex === -1) {
                    setIsBotThinking(false); // Set to false if this is the last message
                }
                return updatedMessages;
            });
            buffer = '';
            setFeedback(prevFeedback => {
                const updatedFeedback = Object.assign(Object.assign({}, prevFeedback), { [receivedMessage.messageID]: {
                        feedbackType: 0,
                        showCommentBox: false,
                        feedbackComment: '',
                        copied: false,
                        supportInvoked: false
                    } });
                return updatedFeedback;
            });
            setSources(prevSources => (Object.assign(Object.assign({}, prevSources), { [messageID]: receivedMessage.sources || [] })));
            // Save updated conversations to local storage
            //saveConversationsToLocalStorage(conversations);
        }
    }
    catch (error) {
        console.error('Error handling received message:', error);
    }
});
export const handleReceiveMessageWrapper = (event, setFeedback, setIsBotThinking, setSources, setSelectedMessageId, setIsTypingComplete, setMessages, scrollToBottom, messagesContainerRef, setConversations, messages, conversationID, userMetadata, conversations) => __awaiter(void 0, void 0, void 0, function* () {
    // Call the original message handling logic
    yield handleReceiveMessage(event, setFeedback, setIsBotThinking, setSources, setSelectedMessageId, setIsTypingComplete, setMessages, scrollToBottom, messagesContainerRef, setConversations, messages, conversationID, userMetadata, conversations, updateOldChats, // Pass the updateOldChats function
    saveConversationsToLocalStorage // Pass the saveConversationsToLocalStorage function
    );
});
export const updateOldChats = (messages, setConversations) => {
    if (messages.length === 0)
        return;
    // Use a Set to track conversation IDs to avoid duplicates
    const conversationIDs = new Set(messages.map(message => message.conversationID));
    setConversations(prevConversations => {
        let updatedConversations = [...prevConversations];
        conversationIDs.forEach(conversationID => {
            var _a;
            const existingConversationIndex = updatedConversations.findIndex(conversation => conversation.conversationID === conversationID);
            if (existingConversationIndex !== -1) {
                // Update existing conversation
                const existingConversation = updatedConversations[existingConversationIndex];
                // Filter messages for the current conversationID
                const messagesForCurrentConversation = messages.filter(message => message.conversationID === conversationID);
                // Update existing messages with new information
                messagesForCurrentConversation.forEach(newMessage => {
                    const existingMessageIndex = existingConversation.messages.findIndex(existingMessage => existingMessage.messageID === newMessage.messageID);
                    if (existingMessageIndex !== -1) {
                        // Update the existing message with new content and sources
                        updatedConversations[existingConversationIndex].messages[existingMessageIndex] = Object.assign(Object.assign({}, existingConversation.messages[existingMessageIndex]), { content: newMessage.content, sources: newMessage.sources || [], used_sources: newMessage.used_sources || [], type: newMessage.type });
                    }
                    else {
                        // If the message doesn't exist, add it as a new message
                        updatedConversations[existingConversationIndex].messages.push(newMessage);
                    }
                });
                // Update endTime
                updatedConversations[existingConversationIndex].endTime = new Date().toISOString();
            }
            else {
                // If conversation does not exist, create a new one
                const newConversation = {
                    type: 'conversation',
                    conversationID: conversationID,
                    userOID: ((_a = messages[0].userMetadata) === null || _a === void 0 ? void 0 : _a.userOID) || '',
                    messages: messages.filter(message => message.conversationID === conversationID),
                    startTime: messages[0].timestamp,
                    endTime: new Date().toISOString()
                };
                updatedConversations.push(newConversation);
            }
        });
        // Save updated conversations to local storage
        localStorage.setItem('conversations', JSON.stringify(updatedConversations));
        return updatedConversations;
    });
};
/**
 * Toggles the sidebar open state.
 * @param {boolean} isSidebarOpen - The current sidebar open state.
 * @param {Dispatch<SetStateAction<boolean>>} setIsSidebarOpen - Function to set sidebar open state.
 */
export const toggleSidebar = (isSidebarOpen, setIsSidebarOpen) => {
    setIsSidebarOpen(!isSidebarOpen);
};
/**
 * Toggles the sources visibility.
 * @param {boolean} showSources - The current sources visibility state.
 * @param {Dispatch<SetStateAction<boolean>>} setShowSources - Function to set sources visibility state.
 */
export const toggleSources = (showSources, setShowSources) => {
    setShowSources(!showSources);
};
/**
 * Strips HTML tags from the content.
 * @param {string} html - The HTML content.
 * @returns {string} - The plain text content.
 */
const stripHtmlTags = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};
/**
 * Copies content to the clipboard.
 * @param {string} messageId - The message ID.
 * @param {string} content - The content to copy.
 * @param {Dispatch<SetStateAction<boolean>>} setIsCopied - Function to set copied state.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to set feedback.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 * @param {string} conversationID - The conversation ID.
 */
export const copyToClipboard = (messageId, content, setIsCopied, setFeedback, setMessages, conversationID) => __awaiter(void 0, void 0, void 0, function* () {
    const plainTextContent = stripHtmlTags(content);
    let updatedFeedbackState = {};
    if (navigator.clipboard) {
        navigator.clipboard.writeText(plainTextContent).then(() => {
            setIsCopied(true);
            setFeedback(prevFeedback => {
                const updatedFeedback = Object.assign(Object.assign({}, prevFeedback), { [messageId]: Object.assign(Object.assign({}, prevFeedback[messageId]), { copied: true // Set copied to true
                     }) });
                setMessages(prevMessages => {
                    const updatedMessages = prevMessages.map(message => message.messageID === messageId ? Object.assign(Object.assign({}, message), { feedback: updatedFeedback[messageId] }) : message);
                    return updatedMessages;
                });
                updatedFeedbackState = updatedFeedback;
                return updatedFeedback;
            });
            // Ensure the state update is complete before making the API call
            setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                // Send feedback to backend
                try {
                    yield axiosInstance.post('/update_feedback', {
                        messageID: messageId,
                        feedback: Object.assign({}, updatedFeedbackState[messageId])
                    }, {
                        params: {
                            conversation_id: conversationID
                        }
                    });
                }
                catch (error) {
                    console.error('Error updating feedback:', error);
                }
            }), 0);
            setTimeout(() => setIsCopied(false), 2000);
        }, err => {
            console.error('Could not copy text: ', err);
        });
    }
    else {
        console.error('Clipboard API not available.');
    }
});
/**
 * Sends an email to support.
 * @param {string} messageID - The message ID.
 * @param {string} userQuestion - The user's question.
 * @param {string} chatbotResponse - The chatbot's response.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to set feedback.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 * @param {string} conversationID - The conversation ID.
 */
export const sendEmailToSupport = (messageID, userQuestion, chatbotResponse, setFeedback, setMessages, conversationID) => __awaiter(void 0, void 0, void 0, function* () {
    const cleanedUserQuestion = stripHtmlTags(userQuestion); // Clean the user's question
    const cleanedChatbotResponse = stripHtmlTags(chatbotResponse); // Clean the chatbot's response
    const body = encodeURIComponent(`Hi, \n\nPlease see the response I received from the chatbot to this question:\n\nMy question: \n${cleanedUserQuestion}\n\nChatbot answer:\n${cleanedChatbotResponse}`);
    const mailtoLink = `mailto:?body=${body}`; // Removed subject and recipient
    window.location.href = mailtoLink;
    let updatedFeedbackState = {};
    // Update feedback state
    setFeedback(prevFeedback => {
        const updatedFeedback = Object.assign(Object.assign({}, prevFeedback), { [messageID]: Object.assign(Object.assign({}, prevFeedback[messageID]), { supportInvoked: true // Set supportInvoked to true
             }) });
        setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(message => message.messageID === messageID ? Object.assign(Object.assign({}, message), { feedback: updatedFeedback[messageID] }) : message);
            return updatedMessages;
        });
        updatedFeedbackState = updatedFeedback;
        return updatedFeedback;
    });
    // Ensure the state update is complete before making the API call
    setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
        // Send feedback to backend
        try {
            yield axiosInstance.post('/update_feedback', {
                messageID: messageID,
                feedback: Object.assign({}, updatedFeedbackState[messageID])
            }, {
                params: {
                    conversation_id: conversationID
                }
            });
        }
        catch (error) {
            console.error('Error updating feedback:', error);
        }
    }), 0);
});
/**
 * Handles thumbs up feedback.
 * @param {string} messageId - The message ID.
 * @param {string} conversationID - The conversation ID.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to set feedback.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 */
export const handleThumbsUp = (messageId, conversationID, setFeedback, setMessages) => __awaiter(void 0, void 0, void 0, function* () {
    let updatedFeedbackState = {};
    setFeedback(prevFeedback => {
        var _a, _b, _c;
        const updatedFeedback = Object.assign(Object.assign({}, prevFeedback), { [messageId]: {
                feedbackType: 1,
                showCommentBox: true,
                feedbackComment: ((_a = prevFeedback[messageId]) === null || _a === void 0 ? void 0 : _a.feedbackComment) || '',
                copied: ((_b = prevFeedback[messageId]) === null || _b === void 0 ? void 0 : _b.copied) || false,
                supportInvoked: ((_c = prevFeedback[messageId]) === null || _c === void 0 ? void 0 : _c.supportInvoked) || false
            } });
        setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(message => message.messageID === messageId ? Object.assign(Object.assign({}, message), { feedback: updatedFeedback[messageId] }) : message);
            return updatedMessages;
        });
        updatedFeedbackState = updatedFeedback;
        return updatedFeedback;
    });
    // Ensure the state update is complete before making the API call
    setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        // Send feedback to backend
        try {
            yield axiosInstance.post('/update_feedback', {
                messageID: messageId,
                feedback: {
                    feedbackType: 1,
                    showCommentBox: true,
                    feedbackComment: ((_a = updatedFeedbackState[messageId]) === null || _a === void 0 ? void 0 : _a.feedbackComment) || '',
                    copied: ((_b = updatedFeedbackState[messageId]) === null || _b === void 0 ? void 0 : _b.copied) || false,
                    supportInvoked: ((_c = updatedFeedbackState[messageId]) === null || _c === void 0 ? void 0 : _c.supportInvoked) || false
                }
            }, {
                params: {
                    conversation_id: conversationID
                }
            });
        }
        catch (error) {
            console.error('Error updating feedback:', error);
        }
    }), 0);
});
/**
 * Handles thumbs down feedback.
 * @param {string} messageId - The message ID.
 * @param {string} conversationID - The conversation ID.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to set feedback.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 */
export const handleThumbsDown = (messageId, conversationID, setFeedback, setMessages) => __awaiter(void 0, void 0, void 0, function* () {
    let updatedFeedbackState = {};
    setFeedback(prevFeedback => {
        var _a, _b, _c;
        const updatedFeedback = Object.assign(Object.assign({}, prevFeedback), { [messageId]: {
                feedbackType: -1,
                showCommentBox: true,
                feedbackComment: ((_a = prevFeedback[messageId]) === null || _a === void 0 ? void 0 : _a.feedbackComment) || '',
                copied: ((_b = prevFeedback[messageId]) === null || _b === void 0 ? void 0 : _b.copied) || false,
                supportInvoked: ((_c = prevFeedback[messageId]) === null || _c === void 0 ? void 0 : _c.supportInvoked) || false
            } });
        setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(message => message.messageID === messageId ? Object.assign(Object.assign({}, message), { feedback: updatedFeedback[messageId] }) : message);
            return updatedMessages;
        });
        updatedFeedbackState = updatedFeedback;
        return updatedFeedback;
    });
    // Ensure the state update is complete before making the API call
    setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
        var _d, _e, _f;
        // Send feedback to backend
        try {
            yield axiosInstance.post('/update_feedback', {
                messageID: messageId,
                feedback: {
                    feedbackType: -1,
                    showCommentBox: true,
                    feedbackComment: ((_d = updatedFeedbackState[messageId]) === null || _d === void 0 ? void 0 : _d.feedbackComment) || '',
                    copied: ((_e = updatedFeedbackState[messageId]) === null || _e === void 0 ? void 0 : _e.copied) || false,
                    supportInvoked: ((_f = updatedFeedbackState[messageId]) === null || _f === void 0 ? void 0 : _f.supportInvoked) || false
                }
            }, {
                params: {
                    conversation_id: conversationID
                }
            });
        }
        catch (error) {
            console.error('Error updating feedback:', error);
        }
    }), 0);
});
/**
 * Handles submitting a comment.
 * @param {string} messageId - The message ID.
 * @param {string} comment - The comment text.
 * @param {{ [key: string]: Feedback }} feedback - The feedback object.
 * @param {Dispatch<SetStateAction<{ [key: string]: Feedback }>>} setFeedback - Function to set feedback.
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to set messages.
 * @param {string} conversationID - The conversation ID.
 */
export const handleSubmitComment = (messageId, comment, feedback, setFeedback, setMessages, conversationID) => {
    const messageFeedback = feedback[messageId];
    let updatedFeedbackState = {};
    if (messageFeedback) {
        setFeedback(prevFeedback => {
            const updatedFeedback = Object.assign(Object.assign({}, prevFeedback), { [messageId]: Object.assign(Object.assign({}, prevFeedback[messageId]), { feedbackComment: comment, showCommentBox: false }) });
            setMessages(prevMessages => {
                const updatedMessages = prevMessages.map(message => message.messageID === messageId ? Object.assign(Object.assign({}, message), { feedback: updatedFeedback[messageId] }) : message);
                return updatedMessages;
            });
            updatedFeedbackState = updatedFeedback;
            return updatedFeedback;
        });
        // Ensure the state update is complete before making the API call
        setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            // Send feedback to backend
            try {
                yield axiosInstance.post('/update_feedback', {
                    messageID: messageId,
                    feedback: Object.assign({}, updatedFeedbackState[messageId])
                }, {
                    params: {
                        conversation_id: conversationID
                    }
                });
            }
            catch (error) {
                console.error('Error updating feedback:', error);
            }
        }), 0);
    }
};
/**
 * Handles WebSocket errors.
 * @param {Event} error - The WebSocket error event.
 */
export const handleWebSocketError = (error) => {
    console.error('WebSocket error:', error);
};
/**
 * Handles WebSocket close event.
 */
export const handleWebSocketClose = () => {
};
/**
 * Handles the key down event in the textarea.
 *
 * @param {KeyboardEvent<HTMLTextAreaElement>} e - The keyboard event.
 * @param {Function} handleSendMessage - Function to handle the sending of a message.
 * @param {Function} resetTextareaHeight - Function to reset the height of the textarea.
 */
export const handleKeyDown = (e, handleSendMessage, resetTextareaHeight) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage(e);
        resetTextareaHeight();
    }
};
/**
 * Handles the form submission.
 *
 * @param {FormEvent} e - The form event.
 * @param {Function} handleSendMessage - Function to handle the sending of a message.
 * @param {Function} resetTextareaHeight - Function to reset the height of the textarea.
 */
export const handleSubmitMessage = (e, handleSendMessage, resetTextareaHeight) => {
    e.preventDefault();
    handleSendMessage(e);
    resetTextareaHeight();
};
/**
 * Resets the height of the textarea.
 *
 * @param {RefObject<HTMLTextAreaElement | null>} textareaRef - The textarea reference.
 */
export const resetTextareaHeight = (textareaRef) => {
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
    }
};
/**
 * Handles the change in the comment input.
 *
 * @param {ChangeEvent<HTMLTextAreaElement>} e - The change event.
 * @param {string} messageId - The ID of the message.
 * @param {Dispatch<SetStateAction<{ [key: string]: string }>>} setCommentInput - Function to set the comment input.
 */
export const handleCommentChange = (e, messageId, setCommentInput) => {
    const value = e.target.value;
    setCommentInput(prev => (Object.assign(Object.assign({}, prev), { [messageId]: value })));
};
/**
 * Handles the submission of a comment.
 *
 * @param {string} messageId - The ID of the message.
 * @param {{ [key: string]: string }} commentInput - The comment input state.
 * @param {Function} handleSubmitComment - Function to handle the submission of a comment.
 * @param {Dispatch<SetStateAction<{ [key: string]: string }>>} setCommentInput - Function to set the comment input.
 */
export const handleCommentSubmit = (messageId, commentInput, handleSubmitComment, setCommentInput) => {
    const comment = commentInput[messageId] || '';
    handleSubmitComment(messageId, comment);
    setCommentInput(prev => (Object.assign(Object.assign({}, prev), { [messageId]: '' }))); // Clear the input after submission
};
// export const fetchLatestConversationID = async (
//    userOid: string,
//    setMessages: Dispatch<SetStateAction<Message[]>>,
//    setConversationID: Dispatch<SetStateAction<string>>,
//    setConversations: Dispatch<SetStateAction<Conversation[]>>,
//    setFeedback: Dispatch<SetStateAction<{ [key: string]: Feedback }>>,
//    startNewChat: (
//       setMessages: Dispatch<SetStateAction<Message[]>>,
//       setFeedback: Dispatch<SetStateAction<{ [key: string]: Feedback }>>,
//       setConversationID: Dispatch<SetStateAction<string>>,
//       ws: WebSocket | null,
//       conversations: Conversation[],
//       setConversations: Dispatch<SetStateAction<Conversation[]>>,
//       userID?: string
//    ) => void,
//    ws: WebSocket | null,
//    userInfo: string,
//    conversations: Conversation[]
// ) => {
//    try {
//       const response = await axiosInstance.get(`/latest_conversation_id`, {
//          params: { user_oid: userInfo }
//       });
//       // If no conversation is found, start a new chat
//       if (response.status === 204 || !response.data.conversation_id) {
//          console.log('No conversation found, starting new chat');
//          startNewChat(setMessages, setFeedback, setConversationID, ws, conversations, setConversations, userInfo);
//          return;
//       }
//       const { conversation_id } = response.data;
//       // Fetch the conversation from blob storage using the conversation ID
//       const conversationResponse = await axiosInstance.get(`/conversation`, {
//          params: { conversation_id }
//       });
//       const latestConversation: Conversation = conversationResponse.data;
//       if (!latestConversation || !latestConversation.messages) {
//          throw new Error('Invalid conversation data');
//       }
//       // Check the timestamp of the last message
//       const lastMessageTimestamp = new Date(
//          latestConversation.messages[latestConversation.messages.length - 1].timestamp
//       );
//       const currentTime = new Date();
//       const timeDifference = (currentTime.getTime() - lastMessageTimestamp.getTime()) / 1000; // Difference in seconds
//       // If the last message was sent within the last 5 minutes (300 seconds), load the conversation
//       if (timeDifference <= 300) {
//          console.log('Loading latest conversation');
//          setMessages(latestConversation.messages);
//          setConversationID(latestConversation.conversationID);
//          // Add the latest conversation to the conversations state
//          setConversations(prevConversations => {
//             const existingConversationIndex = prevConversations.findIndex(
//                conv => conv.conversationID === latestConversation.conversationID
//             );
//             if (existingConversationIndex === -1) {
//                return [...prevConversations, latestConversation]; // Add new conversation
//             }
//             return prevConversations; // Return existing conversations if already present
//          });
//          // Update the feedback state
//          const feedbackState: { [key: string]: Feedback } = {};
//          latestConversation.messages.forEach(message => {
//             feedbackState[message.messageID] = message.feedback;
//          });
//          setFeedback(feedbackState);
//       } else {
//          // If the last message was sent more than 5 minutes ago, start a new chat
//          console.log('Last message was sent more than 5 minutes ago, starting new chat');
//          startNewChat(setMessages, setFeedback, setConversationID, ws, conversations, setConversations, userInfo);
//       }
//    } catch (error) {
//       console.error('Error fetching latest conversation:', error);
//       // Start a new chat if there's an error
//       startNewChat(setMessages, setFeedback, setConversationID, ws, conversations, setConversations, userInfo);
//    }
// };
export const handleSendMessageWrapper = (e, inputMessage, ws, setMessages, setIsBotThinking, setInputMessage, scrollToBottom, messagesContainerRef, isBotTyping, setConversations, conversations, messages, conversationID, userMetadata, userOID // Keep userOID as optional
) => __awaiter(void 0, void 0, void 0, function* () {
    e.preventDefault();
    // Call the original message sending logic
    yield handleSendMessage(e, inputMessage, ws, setMessages, setIsBotThinking, setInputMessage, ref => scrollToBottom(ref, 20), messagesContainerRef, isBotTyping, setConversations, conversations, messages, conversationID, userMetadata);
    // Clear the input message
    setInputMessage('');
});
// Utility functions for local storage
const saveConversationsToLocalStorage = (conversations) => {
    const formattedConversations = conversations.map(conversation => (Object.assign(Object.assign({}, conversation), { messages: conversation.messages.map(message => (Object.assign(Object.assign({}, message), { 
            // Ensure that the content is the formatted HTML content
            content: message.content // This should already be formatted if you're updating it correctly
         }))) })));
    localStorage.setItem('conversations', JSON.stringify(formattedConversations));
};
export const checkUserExists = (email) => __awaiter(void 0, void 0, void 0, function* () {
    /**
     * Check if a user exists based on their email by calling the backend API.
     *
     * @param {string} email - The email of the user to check.
     * @returns {Promise<{ exists: boolean, role: string | null }>}
     *   An object containing:
     *   - exists (boolean): Indicates if the user exists.
     *   - role (string | null): The role of the user if they exist, otherwise null.
     */
    try {
        const response = yield axiosInstance.post('/check_user_email', { email });
        // Assuming the backend returns { exists: true/false, role: 'USER_ROLE' }
        return {
            exists: response.data.exists,
            role: response.data.role || null // Return the role if it exists, otherwise null
        };
    }
    catch (error) {
        console.error('Error checking user email:', error);
        return { exists: false, role: null }; // Assume user does not exist on error
    }
});
export const updateUserRole = (email, role, uid, setUserInfo) => __awaiter(void 0, void 0, void 0, function* () {
    /**
     * Update the user's role in the backend and update the local userInfo state.
     *
     * @param {string} email - The email of the user whose role is to be updated.
     * @param {string} role - The new role to assign to the user.
     * @param {string} uid - The unique identifier of the user.
     * @param {React.Dispatch<React.SetStateAction<UserInfo | null>>} setUserInfo -
     *   The state setter function to update the userInfo state.
     * @returns {Promise<void>}
     *   A promise that resolves when the role is updated successfully.
     */
    try {
        yield axiosInstance.post('/update_user_role', { email, role, uid });
        // Update the userInfo state with the new role
        setUserInfo(prev => (Object.assign(Object.assign({}, prev), { jobTitle: role }))); // Update the jobTitle in userInfo
    }
    catch (error) {
        console.error('Error updating user role:', error);
    }
});
export const mapUserRole = (userJobTitle) => {
    const roleMapping = {
        PLANNER: [
            'Assortment Planner',
            'Competence Lead Planning',
            'Junior Assortment Planner',
            'Market Merchandiser Planner Accessories',
            'Planner-H&M Beauty Private lab',
            'Senior Assortment Planner',
            'Senior Commercial Planning Lead'
        ],
        'PATTERN MAKER': [
            'Business Expert Fit & Size',
            'Competence Lead fit & size',
            'Junior Pattern Cutter',
            'Pattern Cutter',
            'Pattern Maker',
            'Pattern Maker Lead',
            'Pattern Maker - Production',
            'Senior Creative Pattern Cutter',
            'Team Lead Pattern'
        ],
        'BUSINESS CONTROLLER': [
            'Assortment Controller',
            'Business Analyst',
            'Business Controller',
            'Business controller- EB',
            'Central Controller- H&M Beauty',
            'Controller',
            'OMNI Controller',
            'Senior Business Controller',
            'Southern Hemisphere Controller'
        ],
        MANAGER: [
            'Assortment Manager',
            'Assortment Manager Divided',
            'Assortment Manager Visual Direction',
            'Beauty Planner Manager',
            'Buyer Manager',
            'Commercial Planning Manager',
            'Controlling Manager',
            'Director Assortm & Purchasing',
            'Expansion Manager',
            'Function Manager',
            'General Manager',
            'Global business manager',
            'Global R&D Manager',
            'Global Social Media Manager',
            'Global Store Portfolio Merch',
            'Global Store Stylist Lead',
            'Global Visual & Commercial Lead Digital',
            'Global Visual & Commercial Lead Store',
            'Global Visual & Commercial Manager',
            'Global Visual & Commercial Specialist Digital',
            'Global Visual & Commercial Specialist Store',
            'Group Manager',
            'Head of Marketing',
            'Head of Marketing & Com',
            'Head of Marketing & Comms',
            'Head of New Business Development',
            'Head Of Online Studio',
            'Head of Production & Sustainabilty',
            'Head of Sales',
            'Head of Styling',
            'Head of Sustainability & Brand Strategy',
            'Managing Director',
            'MD BU Lifestyle H&M',
            'Operations Manager',
            'People Manager',
            'PR & Communications Manager',
            'PR Manager',
            'Project Manager',
            'Quality controll',
            'Regional Relevance Manager',
            'RETAIL MARKETING MANAGER',
            'Senior Quality Manager',
            'Social Media Manager',
            'Sourcing Manager Food',
            'Strategy Lead',
            'Studio Styling Manager',
            'Sustainability Manager',
            'Team Lead',
            'Unit Lead Engineer Green IT',
            'Wholesales Key Account Manager'
        ],
        'PRODUCT DEVELOPER': [
            'Assortm. Material Responsible',
            'Assortment Product Developer',
            'Component sourcing & development responsible',
            'Competence Lead Product Developer',
            'Junior Assortment Product Developer',
            'Junior Product Developer',
            'Material Responsible',
            'Product Developer',
            'Product Developer Kids Sport',
            'Senior Footwear Product Developer',
            'Senior Product Developer'
        ],
        'PRINT DESIGNER': [
            'Assortment Designer Print',
            'Colour & Print Assistant',
            'Competence Lead Print Design',
            'Designer Illustration',
            'Junior Print Designer',
            'Print Design Lead',
            'Print Designer',
            'Print Lead',
            'Senior Print Designer'
        ],
        'PRODUCT DESIGNER': [
            'Assortment Designer',
            'Assortment Designer Assistant',
            'Assortment Designer Visual Direction',
            'Collection Designer',
            'Competence Lead Design',
            'Concept design',
            'Concept Designer',
            'Designer',
            'Designer Curation',
            'Designer VG & Color',
            'Edition & Campaign Design Lead',
            'Jr Designer',
            'Jr Product Designer',
            'Junior Designer',
            'Junior Product Designer',
            'Product Designer',
            'Senior Designer',
            'Senior Designer Girls',
            'Senior Designer Womenswear',
            'Senior Product designer',
            'SR Designer'
        ],
        'PRODUCT MANAGER': [
            'Assortment Product Manager',
            'Assortment Product Manager',
            'Depart./ Function Responsible',
            'Depart./ Function Responsible',
            'Product Lead',
            'Product Manager',
            'Product Manager External Brands',
            'Product Manager Sport Acc',
            'Senior Product Developer',
            'Senior Product Developer'
        ],
        BUYER: [
            'Assistant Buyer',
            'Buyer',
            'Buyer External Brands',
            'Buyer- external brands',
            'Buyer- external brands Beauty',
            'Buyer- H&M Beauty External Brands',
            'Buyers Admin Assistant',
            'Junior Buyer',
            'Junior Buyer- external brands',
            'Senior Buyer',
            'Senior buyer External brands',
            'Strategic Buyer'
        ],
        OTHER: [
            '3D Project',
            'Accessories Technologist',
            'Account / Operations Manager',
            'Administrator',
            'Architect',
            'Architectural Lead',
            'Art Buyer',
            'Art Director',
            'Atelier Designer',
            'Atelier/Studio Coordinator',
            'Brand Controller',
            'Brand Developer',
            'Brand Development & Packaging Responsible',
            'Brand Manager',
            'Brand manager- private label',
            'Business development manager',
            'Business Expert Design 3D',
            'Business Expert-Data Mangement',
            'Business Operations Manager',
            'Business Unit Director',
            'Celebrities & Influencers Manager',
            'Change Lead Lifestyle Brands',
            'Change Manager',
            'Collection Communication Coordinator',
            'Collection Communication Lead',
            'Commercial Advisor',
            'Comms & Assortment Coordinator',
            'Communication & Product Coordinator',
            'Communication resp.',
            'Communication Responsible',
            'Communication Responsible Manager',
            'Communications Responsible',
            'Competence Lead',
            'Concept Developer',
            'Content Editor',
            'Content Manager',
            'Content Planner',
            'Content Production Manager',
            'Copywriter',
            'Creative Lead',
            'Creative lead- H&M Beauty',
            'Creative Manager',
            'Creative Researcher',
            'Creative Team Manager',
            'Customer Insight analyst',
            'Customer Insight Manager',
            'Customer project manager',
            'Deputy BU Director',
            'Digital Commercial Coordinator',
            'Digital Commercial Specialist',
            'Digital Shopping Exp. Lead',
            'Edition & Campaign Manager',
            'Executive Assistant',
            'Expansion Project Manager',
            'Fashion Editor',
            'Global Online Merchandiser',
            'Global Online Visual Merch',
            'Global Store Stylist',
            'Global Visual & Commercial Lead Digital',
            'Global Visual & Commercial Lead Store',
            'Global Visual & Commercial Manager',
            'Global Visual & Commercial Specialist Digital',
            'Global Visual & Commercial Specialist Store',
            'Head of Atelier',
            'Head of Atelier & Lead Buyer Woven Hard',
            'Head of Buying controlling',
            'Head of Collection Women',
            'Head of Controlling',
            'Head of Creative Projects',
            'Head of Design',
            'Head of Design & Creative',
            'Head of Design & Creative',
            'Head of Ladies Ready to Wear',
            'Head of Marketing',
            'Head of Marketing & Com',
            'Head of Marketing & Comms',
            'Head of Menswear',
            'Head of Menswear Design',
            'Head of New Business Development',
            'Head of Online Studio',
            'Head of Production & Sustainabilty',
            'Head of Sales',
            'Head of Styling',
            'Head of Sustainability & Brand Strategy',
            'ICR',
            'Implementation & Change Responsible',
            'Intern',
            'Internal Communications Lead',
            'Junior Partner Manager',
            'Label Responsible',
            'Lable Responsible',
            'Lead Concept Developer',
            'Learning and Development Specialist',
            'Logistics Specialist',
            'Marketing coordinator',
            'Marketing Planning Lead',
            'Marketing Strategist',
            'Menswear Fashion Editor',
            'Online Stylist',
            'Partner Manager',
            'People Business Partner',
            'PM Marketing & com H&M Beauty',
            'Portfolio manager Exp analyst',
            'PR Specialist',
            'Project Lead',
            'Project manager marketing',
            'Regional Relevance Lead',
            'Sample Coordinator',
            'Social Media Specialist',
            'Space manager H&M Beauty',
            'SupplyChStrategist&Coordinator',
            'Team Lead',
            'Unit Lead Engineer Green IT',
            'Wholesales Key Account Manager'
        ]
    };
    // Check the user's job title and map it to the corresponding role
    let mappedRole = 'OTHER'; // Default to OTHER if no match found
    if (!userJobTitle) {
        console.warn("User job title is empty. Mapped role will be set to 'OTHER'.");
        return mappedRole; // Return "OTHER" if job title is empty
    }
    for (const [key, titles] of Object.entries(roleMapping)) {
        if (titles.includes(userJobTitle)) {
            mappedRole = key;
            break;
        }
    }
    console.info(`Mapped user role '${userJobTitle}' to '${mappedRole}'.`);
    return mappedRole; // Return the mapped role
};
/**
 * Generate a chat title based on the provided conversation object.
 *
 * This function sends a POST request to the '/generate-chat-title' endpoint
 * with the conversation data and returns the generated chat title.
 *
 * @param {Object} conversation - The conversation object containing messages.
 * @returns {Promise<string>} - A promise that resolves to the generated chat title.
 * @throws {Error} - Throws an error if the request fails.
 */
export const generateChatTitle = (conversation) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield axiosInstance.post('/generate-chat-title', conversation);
        return response.data.chat_title; // Return the generated chat title
    }
    catch (error) {
        console.error('Error generating chat title:', error);
        throw error; // Rethrow the error for handling in the component
    }
});
